import * as Api from '@ViewModels';
import moment from 'moment';
import { calculateAge } from './DateUtils';

/** Date format for birthday display (e.g., "8/6") */
const BIRTHDAY_DATE_FORMAT = 'M/D';

/**
 * Determines if an age is a milestone birthday
 * @param age - Age to check
 * @returns True if age is a milestone (21, 65, or decade milestone 30+)
 */
const isMilestoneBirthday = (age: number): boolean => {
	if (age === 21 || age === 65) {
		return true;
	}
	// Check for decade milestones 30 and above
	return age >= 30 && age % 10 === 0;
};

/**
 * Attempts to parse birth date from raw keyFact.value when structured metadata is unavailable
 * @param keyFact - Key fact with raw value
 * @param contact - Contact information
 * @returns Formatted birthday display result or null if parsing fails
 */
const tryParseFromRawValue = (
	keyFact: Api.IKeyFact,
	_contact: Api.IProjectedContact
): IBirthdayDisplayResult | null => {
	const value = keyFact.value;
	if (!value) {
		return null;
	}

	// First try to use structured keyDate.day and keyDate.month if available
	if (keyFact.keyDate?.day && keyFact.keyDate?.month) {
		const day = keyFact.keyDate.day;
		const month = keyFact.keyDate.month;

		// Try to extract year from raw text using flexible patterns
		const yearPattern = /\b(19|20)\d{2}\b/; // Matches years 1900-2099
		const yearMatch = value.match(yearPattern);

		if (yearMatch) {
			const year = yearMatch[0];
			const birthDateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

			// Calculate age
			const age = calculateAge(birthDateString);
			if (age !== null) {
				// Format the date using moment
				const birthDate = moment([parseInt(year), month - 1, day]); // month is 0-indexed in moment
				if (birthDate.isValid()) {
					const formattedDate = birthDate.format(BIRTHDAY_DATE_FORMAT);
					const isMilestone = isMilestoneBirthday(age);
					const text = `Turning ${age} on ${formattedDate}`;
					return { text, isMilestone, age };
				}
			}
		}
	}

	// Fallback to numeric date patterns like "08/15/1999" or "8/15/1999"
	const datePattern = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
	const match = value.match(datePattern);

	if (match) {
		const [, month, day, year] = match;
		const birthDateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

		// Calculate age
		const age = calculateAge(birthDateString);
		if (age !== null) {
			// Format the date using moment
			const [yearNum, monthNum, dayNum] = birthDateString.split('-').map(num => parseInt(num, 10));
			const birthDate = moment([yearNum, monthNum - 1, dayNum]); // month is 0-indexed in moment
			if (birthDate.isValid()) {
				const formattedDate = birthDate.format(BIRTHDAY_DATE_FORMAT);
				const isMilestone = isMilestoneBirthday(age);
				const text = `Turning ${age} on ${formattedDate}`;
				return { text, isMilestone, age };
			}
		}
	}

	// Additional fallback for text formats like "Aug 30, 1956" or "August 30, 1956"
	const textDatePattern =
		/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})\b/i;
	const textMatch = value.match(textDatePattern);

	if (textMatch) {
		const [, monthName, day, year] = textMatch;

		// Convert month name to number
		const monthNames: Record<string, number> = {
			jan: 1,
			january: 1,
			feb: 2,
			february: 2,
			mar: 3,
			march: 3,
			apr: 4,
			april: 4,
			may: 5,
			jun: 6,
			june: 6,
			jul: 7,
			july: 7,
			aug: 8,
			august: 8,
			sep: 9,
			september: 9,
			oct: 10,
			october: 10,
			nov: 11,
			november: 11,
			dec: 12,
			december: 12,
		};

		const month = monthNames[monthName.toLowerCase()];
		if (month) {
			const birthDateString = `${year}-${month.toString().padStart(2, '0')}-${day.padStart(2, '0')}`;

			// Calculate age
			const age = calculateAge(birthDateString);
			if (age !== null) {
				// Format the date using moment
				const birthDate = moment([parseInt(year), month - 1, parseInt(day)]); // month is 0-indexed in moment
				if (birthDate.isValid()) {
					const formattedDate = birthDate.format(BIRTHDAY_DATE_FORMAT);
					const isMilestone = isMilestoneBirthday(age);
					const text = `Turning ${age} on ${formattedDate}`;
					return { text, isMilestone, age };
				}
			}
		}
	}

	return null;
};

export interface IBirthdayDisplayResult {
	text: string;
	isMilestone: boolean;
	age?: number;
}

/**
 * Formats birthday display text according to requirements:
 * - If custom salutation name exists and differs from first name: "{Name} turns {Age} on {Date}"
 * - Otherwise: "Turning {Age} on {Date}"
 * @param keyFact - Key fact containing birthday information
 * @param contact - Contact information
 * @returns Formatted birthday display result with milestone info, or simple string result
 */
export const formatBirthdayDisplay = (
	keyFact: Api.IKeyFact,
	contact: Api.IProjectedContact
): IBirthdayDisplayResult => {
	// Fallback to original value if not valid birthday data
	if (!keyFact || !contact) {
		return { text: keyFact?.value || '', isMilestone: false };
	}

	// Check if this is a birthday key fact
	if (!isBirthdayKeyFact(keyFact)) {
		return { text: keyFact.value, isMilestone: false };
	}

	// Get birthday metadata
	const source = keyFact.source;
	const birthInfo =
		source?.additionalMetadata && 'birthInfo' in source.additionalMetadata
			? (source.additionalMetadata as Api.IBirthInfoKeyFactMetadata).birthInfo
			: null;

	if (!birthInfo?.date) {
		// Try to parse date from raw value as fallback
		const fallbackResult = tryParseFromRawValue(keyFact, contact);
		if (fallbackResult) {
			return fallbackResult;
		}
		return { text: keyFact.value, isMilestone: false };
	}

	// Calculate age
	const age = calculateAge(birthInfo.date);
	if (age === null) {
		return { text: keyFact.value, isMilestone: false };
	}

	// Format the birthday date using moment, avoiding timezone issues
	// Parse date string manually to avoid timezone conversion
	const [year, month, day] = birthInfo.date.split('-').map(num => parseInt(num, 10));
	const birthDate = moment([year, month - 1, day]); // month is 0-indexed in moment
	if (!birthDate.isValid()) {
		return { text: keyFact.value, isMilestone: false };
	}
	const formattedDate = birthDate.format(BIRTHDAY_DATE_FORMAT);

	// Get salutation name from metadata
	const salutationName = birthInfo.name?.trim();
	const contactFirstName = contact.firstName?.trim();

	// Validate names to prevent empty or whitespace-only names
	const hasValidSalutationName = salutationName && salutationName.length > 0;

	// Check if this is a milestone birthday
	const isMilestone = isMilestoneBirthday(age);

	// Determine display format (without HTML tags)
	const text =
		hasValidSalutationName && salutationName !== contactFirstName
			? `${salutationName} turns ${age} on ${formattedDate}`
			: `Turning ${age} on ${formattedDate}`;

	return { text, isMilestone, age };
};

/**
 * Checks if a key fact is a birthday key fact
 * @param keyFact - Key fact to check
 * @returns True if this is a birthday key fact
 */
export const isBirthdayKeyFact = (keyFact: Api.IKeyFact): boolean => {
	return (
		keyFact?.keyDate?.kind === Api.KeyDateKind.Birthday ||
		keyFact?.keyDate?.kind === Api.KeyDateKind.Turning65 ||
		keyFact?.keyDate?.kind === Api.KeyDateKind.Turning72 ||
		keyFact?.keyDate?.kind === Api.KeyDateKind.Turning73 ||
		keyFact?.keyDate?.kind === Api.KeyDateKind.TurningXX ||
		keyFact?.keyDate?.kind === Api.KeyDateKind.TurningXX2
	);
};

/**
 * Extracts birth info from a birthday key fact
 * @param keyFact - Birthday key fact
 * @returns Birth info if available, null otherwise
 */
export const getBirthInfoFromKeyFact = (keyFact: Api.IKeyFact): Api.IKeyFactBirthInfo | null => {
	if (!isBirthdayKeyFact(keyFact)) {
		return null;
	}

	const source = keyFact.source;
	if (!source?.additionalMetadata || !('birthInfo' in source.additionalMetadata)) {
		return null;
	}

	return (source.additionalMetadata as Api.IBirthInfoKeyFactMetadata).birthInfo || null;
};

/**
 * Type guard to check if additional metadata contains birth info
 * @param metadata - Additional metadata to check
 * @returns True if metadata is IBirthInfoKeyFactMetadata
 */
export const isBirthInfoMetadata = (metadata: any): metadata is Api.IBirthInfoKeyFactMetadata => {
	return metadata && typeof metadata === 'object' && 'birthInfo' in metadata;
};
